import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/api';
import {
  AlertCircle,
  CheckCircle,
  Download,
  Loader2,
  RefreshCw,
  X,
} from 'lucide-react';

type ReviewQueueItem = {
  onboarding_id: number;
  client_id: number;
  client_name: string;
  client_email: string;
  review_status: string;
  generated_at: string | null;
  material: { material_id: number; title: string; file_url: string } | null;
};

type ReviewDetail = {
  onboarding_id: number;
  client_id: number;
  client_name: string;
  client_email: string;
  review_status: string;
  generation_status: string;
  generated_at: string | null;
  approved_at: string | null;
  trainer_context: string;
  review_notes: string;
  draft_material: { material_id: number; title: string; file_url: string } | null;
  approved_material: { material_id: number; title: string; file_url: string } | null;
};

const formatDateTime = (value: string | null) => {
  if (!value) return '\u2014';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '\u2014';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const WorkoutPlanReview: React.FC = () => {
  const [queue, setQueue] = useState<ReviewQueueItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Detail / modal state
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ReviewDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  // Regenerate modal state
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [regenContext, setRegenContext] = useState('');
  const [regenNotes, setRegenNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getWorkoutPlanReviewQueue();
      setQueue(data.results);
    } catch (err: any) {
      console.error('Failed to load review queue:', err);
      setError(err?.message || 'Failed to load review queue');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleSelectItem = async (onboardingId: number) => {
    setSelectedId(onboardingId);
    setIsDetailLoading(true);
    setDetail(null);
    try {
      const data = await apiService.getWorkoutPlanReviewDetail(onboardingId);
      setDetail(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load review detail');
    } finally {
      setIsDetailLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!detail) return;
    try {
      setIsSubmitting(true);
      setError(null);
      await apiService.approveWorkoutPlan(detail.onboarding_id);
      setSuccessMsg(`Plan for ${detail.client_name} approved and sent to client.`);
      setSelectedId(null);
      setDetail(null);
      await fetchQueue();
    } catch (err: any) {
      setError(err?.message || 'Failed to approve plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenRegenModal = () => {
    setRegenContext(detail?.trainer_context || '');
    setRegenNotes('');
    setShowRegenModal(true);
  };

  const handleRegenerate = async () => {
    if (!detail) return;
    try {
      setIsSubmitting(true);
      setError(null);
      await apiService.trainerRegenerateWorkoutPlan(
        detail.onboarding_id,
        regenContext,
        regenNotes,
      );
      setSuccessMsg(`Regeneration queued for ${detail.client_name}. You will be emailed when the new draft is ready.`);
      setShowRegenModal(false);
      setSelectedId(null);
      setDetail(null);
      await fetchQueue();
    } catch (err: any) {
      setError(err?.message || 'Failed to regenerate plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-dismiss success message
  useEffect(() => {
    if (!successMsg) return;
    const t = window.setTimeout(() => setSuccessMsg(null), 6000);
    return () => window.clearTimeout(t);
  }, [successMsg]);

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Workout Plan Review</h1>
        <p className="mt-2 text-gray-600">
          Review, approve, or regenerate AI-generated workout plans for your group clients.
        </p>
      </div>

      {/* Success message */}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
            <p className="ml-3 text-sm text-green-800">{successMsg}</p>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Queue list */}
      {queue.length === 0 && !selectedId ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">All caught up</h3>
          <p className="mt-1 text-sm text-gray-500">No workout plans are waiting for your review.</p>
        </div>
      ) : !selectedId ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
          {queue.map((item) => (
            <button
              key={item.onboarding_id}
              type="button"
              onClick={() => handleSelectItem(item.onboarding_id)}
              className="w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
              aria-label={`Review plan for ${item.client_name}`}
              tabIndex={0}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.client_name}</p>
                  <p className="text-xs text-gray-500">{item.client_email}</p>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    Pending review
                  </span>
                  <p className="text-xs text-gray-400 mt-1">
                    Generated {formatDateTime(item.generated_at)}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {/* Detail panel */}
      {selectedId && (
        <div className="space-y-6">
          <button
            type="button"
            onClick={() => { setSelectedId(null); setDetail(null); }}
            className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
            aria-label="Back to queue"
            tabIndex={0}
          >
            &larr; Back to queue
          </button>

          {isDetailLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : detail ? (
            <>
              {/* Client info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900">{detail.client_name}</h2>
                <p className="text-sm text-gray-500">{detail.client_email}</p>
                <div className="mt-3 flex flex-wrap gap-3 text-sm text-gray-600">
                  <span>Generation: <span className="font-medium">{detail.generation_status}</span></span>
                  <span>Review: <span className="font-medium">{detail.review_status}</span></span>
                  {detail.generated_at && (
                    <span>Generated: {formatDateTime(detail.generated_at)}</span>
                  )}
                </div>
              </div>

              {/* Draft PDF */}
              {detail.draft_material && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Draft Plan PDF</h3>
                      <p className="text-sm text-gray-600 mt-1">{detail.draft_material.title}</p>
                    </div>
                    <a
                      href={detail.draft_material.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-primary inline-flex items-center space-x-2"
                      aria-label="View draft PDF"
                      tabIndex={0}
                    >
                      <Download className="w-4 h-4" />
                      <span>View PDF</span>
                    </a>
                  </div>
                </div>
              )}

              {/* Trainer context (if previously set) */}
              {detail.trainer_context && (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <h4 className="text-sm font-medium text-gray-700">Previous trainer context</h4>
                  <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{detail.trainer_context}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={isSubmitting}
                  className="btn-primary inline-flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Approve and send to client"
                  tabIndex={0}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  <span>Approve &amp; Send to Client</span>
                </button>

                <button
                  type="button"
                  onClick={handleOpenRegenModal}
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Regenerate with context"
                  tabIndex={0}
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Regenerate with Context</span>
                </button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Regenerate modal */}
      {showRegenModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-label="Regenerate workout plan"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Regenerate Plan</h3>
              <button
                type="button"
                onClick={() => setShowRegenModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close modal"
                tabIndex={0}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="trainer-context" className="block text-sm font-medium text-gray-700 mb-1">
                  Trainer context / instructions
                </label>
                <textarea
                  id="trainer-context"
                  rows={5}
                  value={regenContext}
                  onChange={(e) => setRegenContext(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="e.g., Client has a knee injury — avoid lunges and deep squats. Prefer upper-body focus on day 2."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This will be passed directly to the AI as high-priority instructions.
                </p>
              </div>

              <div>
                <label htmlFor="regen-notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Internal notes (optional)
                </label>
                <textarea
                  id="regen-notes"
                  rows={2}
                  value={regenNotes}
                  onChange={(e) => setRegenNotes(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Notes for your own reference"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowRegenModal(false)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                tabIndex={0}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRegenerate}
                disabled={isSubmitting}
                className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                tabIndex={0}
                aria-label="Submit regeneration request"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span>Regenerate</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkoutPlanReview;
